import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
  AlertTriangle, CheckCircle2, Loader2, RefreshCw, UserCheck, UserX, Wand2,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import { toast } from 'sonner';
import { AssignCommercialDialog } from './companies/AssignCommercialDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin/commercials`;

const Kpi = ({ label, value, tone, icon: Icon, testId }) => (
  <div className={`rounded-xl border p-3 ${tone}`} data-testid={testId}>
    <div className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide opacity-70">
      <Icon className="h-3.5 w-3.5" /> {label}
    </div>
    <div className="mt-1 text-xl font-bold">{value}</div>
  </div>
);

/** Couverture commerciale : clients & opérations sans commercial + rattrapage en 1 clic. */
export const CommercialCoverageCard = ({ canManage = false }) => {
  const [coverage, setCoverage] = useState(null);
  const [orphans, setOrphans] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reconciling, setReconciling] = useState(false);
  const [assignTarget, setAssignTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: cov }, { data: orp }] = await Promise.all([
        axios.get(`${API}/coverage`),
        axios.get(`${API}/orphan-operations`),
      ]);
      setCoverage(cov);
      setOrphans(orp);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Couverture commerciale indisponible');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const reconcile = async () => {
    setReconciling(true);
    try {
      const { data } = await axios.post(`${API}/reconcile-operations`);
      toast.success(`${data.fixed} opération(s) rattachée(s) à leur commercial`, {
        description: data.stillOrphan > 0
          ? `${data.stillOrphan} opération(s) restent sans commercial (société non assignée)`
          : 'Plus aucune opération orpheline rattrapable',
      });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Rattrapage impossible');
    } finally {
      setReconciling(false);
    }
  };

  if (loading) return <Skeleton className="h-64 w-full rounded-xl" />;

  const unassignedOps = coverage?.unassignedOperations ?? 0;
  const fixable = orphans?.fixableOperations ?? 0;
  const lastRun = coverage?.lastReconciliation;

  return (
    <Card data-testid="commercial-coverage-card">
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              <UserCheck className="h-4 w-4 text-emerald-600" /> Couverture commerciale
            </CardTitle>
            <CardDescription className="text-xs">
              Chaque opération doit être créditée à un commercial. Le rattrapage tourne
              automatiquement chaque nuit (02h30 Abidjan) et peut être lancé à la demande.
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" className="h-8 text-xs" onClick={load} data-testid="coverage-refresh-btn">
              <RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Actualiser
            </Button>
            {canManage && (
              <Button
                size="sm"
                className="h-8 bg-emerald-600 text-xs hover:bg-emerald-700"
                onClick={reconcile}
                disabled={reconciling || fixable === 0}
                title={fixable === 0 ? 'Aucune opération rattrapable' : undefined}
                data-testid="coverage-reconcile-btn"
              >
                {reconciling ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Wand2 className="mr-1.5 h-3.5 w-3.5" />}
                Réconcilier ({fixable})
              </Button>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <Kpi
            label="Clients couverts" icon={CheckCircle2} testId="coverage-kpi-clients"
            value={`${coverage?.coveragePct ?? 0}%`}
            tone={(coverage?.coveragePct ?? 0) >= 90 ? 'border-emerald-200 bg-emerald-50 text-emerald-900' : 'border-amber-200 bg-amber-50 text-amber-900'}
          />
          <Kpi
            label="Clients sans commercial" icon={UserX} testId="coverage-kpi-clients-without"
            value={coverage?.withoutCommercial ?? 0}
            tone={(coverage?.withoutCommercial ?? 0) === 0 ? 'border-gray-200 bg-white text-gray-900' : 'border-rose-200 bg-rose-50 text-rose-900'}
          />
          <Kpi
            label="Ops créditées" icon={CheckCircle2} testId="coverage-kpi-ops"
            value={`${coverage?.opsCoveragePct ?? 0}%`}
            tone={(coverage?.opsCoveragePct ?? 0) >= 95 ? 'border-emerald-200 bg-emerald-50 text-emerald-900' : 'border-amber-200 bg-amber-50 text-amber-900'}
          />
          <Kpi
            label="Ops sans commercial" icon={AlertTriangle} testId="coverage-kpi-ops-without"
            value={unassignedOps}
            tone={unassignedOps === 0 ? 'border-gray-200 bg-white text-gray-900' : 'border-rose-200 bg-rose-50 text-rose-900'}
          />
        </div>

        {lastRun && (
          <p className="text-[11px] text-gray-500" data-testid="coverage-last-run">
            Dernier rattrapage : {new Date(lastRun.runAt).toLocaleString('fr-FR')} ({lastRun.triggeredBy === 'cron' ? 'automatique' : 'manuel'})
            — {lastRun.fixed} corrigée(s), {lastRun.stillOrphan} restante(s).
          </p>
        )}

        {/* Rattrapables */}
        {fixable > 0 && (
          <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-3" data-testid="coverage-fixable-block">
            <p className="text-xs font-semibold text-amber-900">
              {fixable} opération(s) rattrapables — la société a un commercial, l&apos;opération ne l&apos;a pas
            </p>
            <div className="mt-2 space-y-1">
              {(orphans?.fixableCompanies || []).slice(0, 8).map((c) => (
                <div key={c.companyName} className="flex items-center justify-between gap-2 text-xs text-amber-900">
                  <span className="truncate font-medium">{c.companyName}</span>
                  <span className="flex shrink-0 items-center gap-2">
                    <Badge variant="outline" className="border-amber-300 bg-white text-[10px]">{c.operationsCount} op.</Badge>
                    <span className="text-[11px] opacity-80">→ {c.commercialName || 'commercial de la société'}</span>
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Sociétés sans aucun commercial */}
        <div>
          <p className="mb-2 text-xs font-semibold text-gray-700">
            Sociétés sans commercial ({orphans?.orphanCompanies?.length || 0})
            {(orphans?.orphanOperations || 0) > 0 && (
              <span className="ml-1 font-normal text-gray-500">— {orphans.orphanOperations} opération(s) concernée(s)</span>
            )}
          </p>
          {(orphans?.orphanCompanies || []).length === 0 ? (
            <p className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-800" data-testid="coverage-no-orphans">
              Toutes les sociétés ayant des opérations ont un commercial assigné.
            </p>
          ) : (
            <div className="divide-y divide-gray-100 rounded-xl border border-gray-200" data-testid="coverage-orphan-list">
              {orphans.orphanCompanies.slice(0, 12).map((c) => (
                <div key={`${c.companyName}-${c.clientId || 'na'}`} className="flex items-center justify-between gap-3 px-3 py-2">
                  <div className="min-w-0">
                    <p className="truncate text-xs font-medium text-gray-900">{c.companyName}</p>
                    <p className="text-[11px] text-gray-500">{c.operationsCount} opération(s) non créditée(s)</p>
                  </div>
                  {canManage && c.clientId && (
                    <Button
                      variant="outline" size="sm" className="h-7 shrink-0 text-[11px]"
                      onClick={() => setAssignTarget({ id: c.clientId, companyName: c.companyName })}
                      data-testid={`coverage-assign-btn-${c.clientId}`}
                    >
                      <UserCheck className="mr-1 h-3 w-3" /> Assigner
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </CardContent>

      <AssignCommercialDialog
        open={!!assignTarget}
        onOpenChange={(v) => !v && setAssignTarget(null)}
        client={assignTarget}
        onSuccess={() => { setAssignTarget(null); load(); }}
      />
    </Card>
  );
};

export default CommercialCoverageCard;
